import React, { useContext, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatCurrency } from "../config/siteConfig";
import "../css/cartpage.scss";

const toNumber = (value) => {
  const parsedValue = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const scrollToTop = () => {
  if (typeof window !== "undefined") window.scrollTo({ top: 0 });
};

const CartPage = () => {
  const navigate = useNavigate();
  const {
    cartItems,
    removeFromCart,
    updateQuantity,
    toggleWishlist,
    isWishlisted,
  } = useContext(CartContext);

  const cartLines = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find(
            (product) => String(product.id) === String(item.id)
          ),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = useMemo(
    () =>
      cartLines.reduce(
        (total, item) =>
          total + toNumber(item.product.price) * item.quantity,
        0
      ),
    [cartLines]
  );

  const shippingRate = toNumber(siteConfig.flatShippingRate);
  const shipping = subtotal === 0 ? 0 : shippingRate;
  const total = subtotal + shipping;

  const handleCheckout = () => {
    if (!cartLines.length) return;
    navigate("/checkout");
    scrollToTop();
  };

  if (!cartLines.length) {
    return (
      <main className="cart-page cart-page--empty">
        <section className="cart-page__empty" aria-labelledby="empty-cart-title">
          <p className="cart-page__eyebrow">Your cart</p>
          <h1 id="empty-cart-title">Nothing here yet.</h1>
          <p>
            Explore considered tools for writing, drawing, and everyday desk
            rituals.
          </p>
          <Link
            to="/shop"
            onClick={scrollToTop}
            className="cart-page__primary-link"
          >
            Shop stationery
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <header className="cart-page__header">
        <p className="cart-page__eyebrow">Your cart</p>
        <h1>Ready for your desk</h1>
        <p>
          Tracked standard delivery is {formatCurrency(shippingRate)} per order.
        </p>
      </header>

      <div className="cart-page__layout">
        <section className="cart-page__items" aria-label="Cart items">
          {cartLines.map((item) => {
            const { product } = item;
            const productImage = product.image || product.images?.[0];
            const wishlisted = isWishlisted(product.id);

            return (
              <article
                className="cart-item"
                key={`${item.id}-${item.size}-${item.color}`}
              >
                <Link
                  className="cart-item__image-link"
                  to={`/shopDetails/${product.id}`}
                  onClick={scrollToTop}
                  aria-label={`View ${product.name}`}
                >
                  {productImage ? (
                    <img
                      src={productImage}
                      alt={product.name}
                      loading="lazy"
                    />
                  ) : (
                    <span aria-hidden="true">IL</span>
                  )}
                </Link>

                <div className="cart-item__content">
                  <div className="cart-item__heading">
                    <div>
                      <p className="cart-item__category">{product.category}</p>
                      <h2>
                        <Link
                          to={`/shopDetails/${product.id}`}
                          onClick={scrollToTop}
                        >
                          {product.name}
                        </Link>
                      </h2>
                    </div>
                    <p className="cart-item__unit-price">
                      {formatCurrency(toNumber(product.price))}
                    </p>
                  </div>

                  {(item.color || item.size) && (
                    <dl className="cart-item__options">
                      {item.color && (
                        <div>
                          <dt>Color</dt>
                          <dd>{item.color}</dd>
                        </div>
                      )}
                      {item.size && (
                        <div>
                          <dt>Option</dt>
                          <dd>{item.size}</dd>
                        </div>
                      )}
                    </dl>
                  )}

                  <div className="cart-item__footer">
                    <div
                      className="quantity-control"
                      role="group"
                      aria-label={`Quantity for ${product.name}`}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          updateQuantity(
                            item.id,
                            item.size,
                            item.color,
                            item.quantity - 1
                          )
                        }
                        disabled={item.quantity <= 1}
                        aria-label={`Decrease ${product.name} quantity`}
                      >
                        −
                      </button>
                      <output aria-live="polite">{item.quantity}</output>
                      <button
                        type="button"
                        onClick={() =>
                          updateQuantity(
                            item.id,
                            item.size,
                            item.color,
                            Math.min(product.stock, item.quantity + 1)
                          )
                        }
                        disabled={product.stock === 0 || item.quantity >= product.stock}
                        aria-label={`Increase ${product.name} quantity`}
                      >
                        +
                      </button>
                    </div>

                    <div className="cart-item__actions">
                      <button
                        type="button"
                        className="cart-item__text-button"
                        aria-pressed={wishlisted}
                        onClick={() => toggleWishlist(product.id)}
                      >
                        {wishlisted ? "Saved to wishlist" : "Save to wishlist"}
                      </button>
                      <button
                        type="button"
                        className="cart-item__text-button"
                        onClick={() =>
                          removeFromCart(
                            item.id,
                            item.size,
                            item.color
                          )
                        }
                      >
                        Remove
                      </button>
                    </div>

                    <p className="cart-item__line-total">
                      {formatCurrency(
                        toNumber(product.price) * item.quantity
                      )}
                    </p>
                  </div>
                </div>
              </article>
            );
          })}

          <Link
            to="/shop"
            onClick={scrollToTop}
            className="cart-page__continue-link"
          >
            ← Continue shopping
          </Link>
        </section>

        <aside className="cart-summary" aria-labelledby="order-summary-title">
          <h2 id="order-summary-title">Order summary</h2>
          <dl>
            <div>
              <dt>Subtotal</dt>
              <dd>{formatCurrency(subtotal)}</dd>
            </div>
            <div>
              <dt>Standard shipping</dt>
              <dd>{shipping === 0 ? "Free" : formatCurrency(shipping)}</dd>
            </div>
            <div className="cart-summary__total">
              <dt>Total</dt>
              <dd>{formatCurrency(total)}</dd>
            </div>
          </dl>
          <p className="cart-summary__note">
            Taxes, where applicable, are confirmed before payment.
          </p>
          <button
            className="cart-summary__checkout"
            type="button"
            onClick={handleCheckout}
          >
            Continue to checkout
          </button>
          <p className="cart-summary__shipping-note">
            Card details are entered only on our payment partner&apos;s secure page.
          </p>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
